'use client'

import { Box, Divider, IconButton } from '@mui/material'
import { FlightRouteInfo } from '@/src/types/flight'
import FareSelectionHeaderTitle from './FareSelectionHeaderTitle'
import FareSelectionFlightSelector from './FareSelectionFlightSelector'
import FareSelectionFilters from './FareSelectionFilters'
import { FarePlanState } from '../types'
import { useEffect, useState } from 'react'
import { ArrowBack, ArrowForward } from '@mui/icons-material'

export type FareSelectionHeaderProps = {
    handleClose: () => void
    selectedFilters: string[]
    onFilterChange: (label: string, checked: boolean) => void
    selectedFlightIndex: number
    fareFamilySectors?: FlightRouteInfo[]
    setFarePlanState: React.Dispatch<React.SetStateAction<FarePlanState>>
    plansScrollRef: React.RefObject<HTMLDivElement>
    plansCount: number
}

export default function FareSelectionHeader(props: FareSelectionHeaderProps) {
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return; // skip during SSR

        const checkScrollability = () => {
            const container = props.plansScrollRef.current;
            if (!container) return;

            const hasOverflow = container.scrollWidth > container.clientWidth;
            const isAtStart = container.scrollLeft === 0;
            const isAtEnd =
                container.scrollLeft >= container.scrollWidth - container.clientWidth - 1;

            setCanScrollLeft(hasOverflow && !isAtStart);
            setCanScrollRight(hasOverflow && !isAtEnd);
        };

        // Run after paint (ensures DOM is ready)
        const raf = requestAnimationFrame(checkScrollability);

        const container = props.plansScrollRef.current;
        if (container) {
            container.addEventListener('scroll', checkScrollability);
            window.addEventListener('resize', checkScrollability);
        }

        return () => {
            cancelAnimationFrame(raf);
            if (container) {
                container.removeEventListener('scroll', checkScrollability);
            }
            window.removeEventListener('resize', checkScrollability);
        };
    }, [props.plansScrollRef, props.plansCount]);


    const handleScroll = (direction: 'left' | 'right') => {
        const container = props.plansScrollRef.current;
        if (!container) return;

        const scrollOffset = 220;
        const scrollOptions: ScrollToOptions = {
            left: direction === 'right'
                ? container.scrollLeft + scrollOffset
                : container.scrollLeft - scrollOffset,
            behavior: 'smooth'
        };

        container.scrollTo(scrollOptions);
    };


    const showArrows = canScrollLeft || canScrollRight;
    return (
        <Box>
            <FareSelectionHeaderTitle handleClose={props.handleClose} />
            <FareSelectionFlightSelector
                selectedFlightIndex={props.selectedFlightIndex}
                setSelectedFlightIndex={(index : number) => props.setFarePlanState(prev => ({ ...prev, selectedFlightIndex: index }))}
                fareFamilySectors={props.fareFamilySectors}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <FareSelectionFilters
                    selectedFilters={props.selectedFilters}
                    onFilterChange={props.onFilterChange}
                />
                {showArrows && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px',
                        marginRight: '8px'
                    }}>
                        <IconButton
                            onClick={() => canScrollLeft && handleScroll('left')}
                            disabled={!canScrollLeft}
                            sx={{
                                color: canScrollLeft ? 'primary.main' : 'neutral.300',
                                cursor: canScrollLeft ? 'pointer' : 'not-allowed',
                                '&:disabled': {
                                    color: 'neutral.300',
                                    cursor: 'not-allowed',
                                },
                            }}
                        >
                            <ArrowBack sx={{ fontSize: 32 }} />
                        </IconButton>

                        <IconButton
                            onClick={() => canScrollRight && handleScroll('right')}
                            disabled={!canScrollRight}
                            sx={{
                                color: canScrollRight ? 'primary.main' : 'neutral.300',
                                cursor: canScrollRight ? 'pointer' : 'not-allowed',
                                '&:disabled': {
                                    color: 'neutral.300',
                                    cursor: 'not-allowed',
                                },
                            }}
                        >
                            <ArrowForward sx={{ fontSize: 32 }} />
                        </IconButton>

                    </Box>
                )}
            </Box>
            <Divider />
        </Box>
    )
}